import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../hooks/index.js';
import { usePageAccess } from '../../hooks/usePageAccess.js';
import type { PageKey } from '@cortex/domain-product-innovation-lab-types';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * アクセス制御の対象ページキー。サイドバー表示（useNavGroups）と同一の SSOT
   * （canAccessPage）を参照するため、メニュー表示可否とページアクセス可否が必ず一致する。
   */
  pageKey: PageKey;
  /**
   * アクセス不可時のリダイレクト先。既定は全員（external 含む）アクセス可能な /my-page。
   * 旧既定の /kpi は内部メンバー限定ページのため、external が弾かれた際にリダイレクト
   * ループを起こす。ループを避けるため誰でも開ける /my-page を既定にする。
   */
  fallbackPath?: string;
}

/**
 * 権限ベースのルート保護コンポーネント
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 認可ガード。canAccessPage(SSOT) でアクセス可否を判定し、不足時はフォールバックパスへリダイレクト。ロード中・未認可時は子要素を非表示にするPessimisticアプローチ
 * @graph-connects useAuth [calls] 認証ロード状態を取得
 * @graph-connects usePageAccess [calls] サイドバーと共通の SSOT でページアクセス可否を判定
 */
export function ProtectedRoute({
  children,
  pageKey,
  fallbackPath = '/my-page',
}: ProtectedRouteProps) {
  const { isLoading } = useAuth();
  const { canAccess } = usePageAccess();
  const navigate = useNavigate();

  const hasAccess = canAccess(pageKey);
  // フォールバック redirect は 1 回しか撃たない。auth state が transition 中に
  // 多重に変動して effect が再発火しても navigate は再呼び出ししない。
  // 連続 navigate は TanStack Router Transitioner の setIsTransitioning を
  // 連発させ React の setState 上限を超えて Minified React error #185 を起こす
  // （docs/web/product-innovation-lab.md, PR #368）。
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (navigatedRef.current) return;
    if (!isLoading && !hasAccess) {
      navigatedRef.current = true;
      void navigate({ to: fallbackPath });
    }
  }, [isLoading, hasAccess, navigate, fallbackPath]);

  // ロード中または権限がない場合は何も表示しない（Pessimistic approach）
  if (isLoading || !hasAccess) {
    return null;
  }

  return <>{children}</>;
}
